/**
 * Bond Mentor — Signal Engine
 *
 * The brain of Bond Mentor. Takes all bond market data inputs and computes
 * the Master Signal along with recommended action levels.
 *
 * SIGNAL THRESHOLDS (per handoff spec 27_Signal_Math_Formula_Detail):
 *   0-24  = GREEN  (All Clear)
 *   25-49 = YELLOW (Caution)
 *   50-74 = ORANGE (Elevated)
 *   75-100 = RED   (Danger)
 */

// ============================================
// SIGNAL LEVELS — 4-tier system
// ============================================
export const SIGNAL = {
  GREEN: 'GREEN',
  YELLOW: 'YELLOW',
  ORANGE: 'ORANGE',
  RED: 'RED',
};

// Signal colors for UI rendering
export const SIGNAL_COLORS = {
  GREEN: '#22c55e',
  YELLOW: '#eab308',
  ORANGE: '#f97316',
  RED: '#ef4444',
};

// ============================================
// ACTION LEVELS (graduated from safest to most aggressive)
// ============================================
export const ACTION_LEVELS = {
  FULLY_INVESTED: {
    key: 'FULLY_INVESTED',
    label: 'Stay Fully Invested',
    exposure: '100%',
    signal: SIGNAL.GREEN,
    description: 'All bond market signals are clear. Stay the course with your current investment strategy. No defensive action needed.',
    color: '#22c55e',
  },
  TIGHTEN_STOPS: {
    key: 'TIGHTEN_STOPS',
    label: 'Tighten Stops',
    exposure: '90%',
    signal: SIGNAL.YELLOW,
    description: 'Caution signals appearing. Keep your positions but tighten your stop-losses. Hold off on new aggressive buys until the picture clears.',
    color: '#a3e635',
  },
  REDUCE_75: {
    key: 'REDUCE_75',
    label: 'Reduce to 75% Exposure',
    exposure: '75%',
    signal: SIGNAL.YELLOW,
    description: 'Multiple warning signals active. Take some profits and move 25% to cash or short-term Treasuries. Preserve your gains.',
    color: '#eab308',
  },
  REDUCE_50: {
    key: 'REDUCE_50',
    label: 'Reduce to 50% Exposure',
    exposure: '50%',
    signal: SIGNAL.ORANGE,
    description: 'Elevated bond market warnings. Raise significant cash. Move half your portfolio to safety. Capital preservation is now a priority.',
    color: '#f97316',
  },
  DEFENSIVE_25: {
    key: 'DEFENSIVE_25',
    label: 'Defensive Mode — 25% or Less',
    exposure: '25%',
    signal: SIGNAL.RED,
    description: 'Major danger signals across the bond market. Only keep your highest-conviction positions. Most of your capital should be in cash or short-term Treasuries.',
    color: '#ef4444',
  },
  MAX_DEFENSE: {
    key: 'MAX_DEFENSE',
    label: 'Maximum Defense',
    exposure: '0-10%',
    signal: SIGNAL.RED,
    description: 'Extremely rare, historic-level stress in the bond market. Move to near-full cash or short-term Treasuries. Protect everything. This level has only triggered a handful of times in market history.',
    color: '#dc2626',
  },
};

// ============================================
// SUB-SIGNAL CATEGORIES
// ============================================
export const SUB_SIGNALS = {
  YIELD_CURVE: 'yieldCurve',
  CREDIT_SPREADS: 'creditSpreads',
  FED_POSTURE: 'fedPosture',
  VIGILANTE_ACTIVITY: 'vigilanteActivity',
  VOLATILITY: 'volatility',
  LIQUIDITY: 'liquidity',
  MONEY_FLOWS: 'moneyFlows',
  GLOBAL_BONDS: 'globalBonds',
};

// ============================================
// COMPUTE MASTER SIGNAL
// ============================================

/**
 * Takes all sub-signal scores (0-100 where 0=safest, 100=most dangerous)
 * and computes the master signal with weighted importance.
 *
 * Weights per handoff spec 04_Signal_Weighting_Suggestions:
 *   Yield Curve = 20%, Credit Spreads = 20%, Fed = 15%,
 *   Vigilantes = 15%, Volatility = 10%, Liquidity = 10%,
 *   Money Flows = 5%, Global Bonds = 5%
 */
export function getRiskRegime(score) {
  const safeScore = Number.isFinite(Number(score)) ? Math.round(Number(score)) : 50;
  if (safeScore <= 24) return { key: 'ALL_CLEAR', label: 'ALL CLEAR', min: 0, max: 24, nextTrigger: 25, signal: SIGNAL.GREEN };
  if (safeScore <= 49) return { key: 'CAUTION', label: 'CAUTION', min: 25, max: 49, nextTrigger: 50, signal: SIGNAL.YELLOW };
  if (safeScore <= 74) return { key: 'ELEVATED', label: 'ELEVATED RISK', min: 50, max: 74, nextTrigger: 75, signal: SIGNAL.ORANGE };
  return { key: 'DANGER', label: 'DANGER', min: 75, max: 100, nextTrigger: null, signal: SIGNAL.RED };
}

function getSignalBreadth(subSignals) {
  const values = Object.values(subSignals || {})
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  return {
    total: values.length,
    clearCount: values.filter((value) => value < 25).length,
    cautionOrWorse: values.filter((value) => value >= 25).length,
    elevatedOrWorse: values.filter((value) => value >= 50).length,
    dangerOrWorse: values.filter((value) => value >= 75).length,
  };
}

function getBreadthFloor(breadth) {
  let floor = 0;
  if (breadth.cautionOrWorse >= 4) floor = Math.max(floor, 32);
  if (breadth.cautionOrWorse >= 5) floor = Math.max(floor, 35);
  if (breadth.elevatedOrWorse >= 2) floor = Math.max(floor, 45);
  if (breadth.elevatedOrWorse >= 3) floor = Math.max(floor, 55);
  if (breadth.dangerOrWorse >= 1 && breadth.elevatedOrWorse >= 2) floor = Math.max(floor, 65);
  return floor;
}

export function computeMasterSignal(subSignals) {
  const weights = {
    [SUB_SIGNALS.YIELD_CURVE]: 0.20,
    [SUB_SIGNALS.CREDIT_SPREADS]: 0.20,
    [SUB_SIGNALS.FED_POSTURE]: 0.15,
    [SUB_SIGNALS.VIGILANTE_ACTIVITY]: 0.15,
    [SUB_SIGNALS.VOLATILITY]: 0.10,
    [SUB_SIGNALS.LIQUIDITY]: 0.10,
    [SUB_SIGNALS.MONEY_FLOWS]: 0.05,
    [SUB_SIGNALS.GLOBAL_BONDS]: 0.05,
  };

  let compositeScore = 0;
  let totalWeight = 0;

  for (const [key, weight] of Object.entries(weights)) {
    if (subSignals[key] !== undefined && subSignals[key] !== null) {
      compositeScore += subSignals[key] * weight;
      totalWeight += weight;
    }
  }

  // Normalize if not all signals are available
  if (totalWeight > 0 && totalWeight < 1) {
    compositeScore = compositeScore / totalWeight;
  }

  const rawWeightedCompositeScore = Math.round((compositeScore + Number.EPSILON) * 100) / 100;
  const riskBreadth = getSignalBreadth(subSignals);
  const breadthFloor = getBreadthFloor(riskBreadth);
  const adjustedCompositeScore = Math.max(rawWeightedCompositeScore, breadthFloor);

  const exactCompositeScore = Math.round((adjustedCompositeScore + Number.EPSILON) * 100) / 100;
  const displayCompositeScore = Math.round(exactCompositeScore);

  // Determine signal — 4-tier per handoff spec
  let signal;
  if (displayCompositeScore <= 24) {
    signal = SIGNAL.GREEN;
  } else if (displayCompositeScore <= 49) {
    signal = SIGNAL.YELLOW;
  } else if (displayCompositeScore <= 74) {
    signal = SIGNAL.ORANGE;
  } else {
    signal = SIGNAL.RED;
  }

  // Determine action level — 6 graduated tiers
  let actionLevel;
  if (displayCompositeScore <= 24) {
    actionLevel = ACTION_LEVELS.FULLY_INVESTED;
  } else if (displayCompositeScore <= 42) {
    actionLevel = ACTION_LEVELS.TIGHTEN_STOPS;
  } else if (displayCompositeScore <= 55) {
    actionLevel = ACTION_LEVELS.REDUCE_75;
  } else if (displayCompositeScore <= 62) {
    actionLevel = ACTION_LEVELS.REDUCE_50;
  } else if (displayCompositeScore <= 82) {
    actionLevel = ACTION_LEVELS.DEFENSIVE_25;
  } else {
    actionLevel = ACTION_LEVELS.MAX_DEFENSE;
  }

  return {
    signal,
    compositeScore: displayCompositeScore,
    exactCompositeScore,
    rawWeightedCompositeScore,
    breadthFloorApplied: breadthFloor > rawWeightedCompositeScore ? breadthFloor : null,
    roundingMethod: 'Math.round',
    riskRegime: getRiskRegime(displayCompositeScore),
    riskBreadth,
    actionLevel,
    subSignals,
    timestamp: new Date(),
  };
}

/**
 * Generate a plain-English summary of the current signal state.
 */
export function generateSignalSummary(masterSignal) {
  const { signal, compositeScore } = masterSignal;
  const subs = masterSignal.subSignals || {};

  if (signal === SIGNAL.GREEN) {
    return {
      headline: 'All Clear — Core Bond Market Signals Are Favorable',
      body: `Core bond-market stress readings are currently calm enough to support a risk-on stance. This does not mean every macro risk is gone; it means the weighted signal system is not detecting broad financial stress at this time. Continue monitoring long-term yield pressure, inflation risk, and volatility. Composite risk score: ${compositeScore}/100.`,
      tone: 'positive',
    };
  }

  if (signal === SIGNAL.YELLOW) {
    const triggers = [];
    if (subs.yieldCurve > 30) triggers.push('yield curve is flattening or showing stress');
    if (subs.creditSpreads > 30) triggers.push('credit spreads are starting to widen');
    if (subs.fedPosture > 30) triggers.push('the Fed and bond market appear to disagree');
    if (subs.vigilanteActivity > 30) triggers.push('bond vigilante activity is picking up');
    if (subs.volatility > 30) triggers.push('bond volatility is elevated relative to stock volatility');

    const triggerText = triggers.length > 0
      ? `Specifically, ${triggers.join(', ')}.`
      : 'Several indicators are at borderline levels.';

    return {
      headline: 'Caution — Some Warning Signals Appearing',
      body: `The bond market is showing some early caution signs that deserve your attention. ${triggerText} This doesn't mean a crash is imminent, but historically, these conditions preceded periods of increased stock market turbulence. Smart money is beginning to reposition. Consider tightening your risk management. Composite risk score: ${compositeScore}/100.`,
      tone: 'cautious',
    };
  }

  if (signal === SIGNAL.ORANGE) {
    const triggers = [];
    if (subs.yieldCurve > 50) triggers.push('the yield curve is showing significant stress');
    if (subs.creditSpreads > 50) triggers.push('credit spreads are widening into stressed territory');
    if (subs.fedPosture > 50) triggers.push('the Fed is falling behind market expectations');
    if (subs.vigilanteActivity > 50) triggers.push('bond vigilantes are actively protesting');
    if (subs.volatility > 50) triggers.push('bond volatility is at elevated levels');
    if (subs.liquidity > 50) triggers.push('financial system liquidity is thinning');

    const triggerText = triggers.length > 0
      ? `Key warning signals: ${triggers.join('; ')}.`
      : 'Multiple indicators have crossed into warning territory.';

    return {
      headline: 'Elevated Risk — Multiple Warning Signals Active',
      body: `The bond market is sending elevated stress signals. ${triggerText} This level of stress has historically preceded significant stock market pullbacks. Smart money has moved substantially to the sidelines. Reduce exposure and prioritize capital preservation. Composite risk score: ${compositeScore}/100.`,
      tone: 'elevated',
    };
  }

  // RED
  const triggers = [];
  if (subs.yieldCurve > 60) triggers.push('the yield curve is inverted or in a dangerous steepening pattern');
  if (subs.creditSpreads > 60) triggers.push('credit spreads are blowing out, signaling corporate stress');
  if (subs.fedPosture > 60) triggers.push('the Fed is behind the curve and the bond market knows it');
  if (subs.vigilanteActivity > 60) triggers.push('bond vigilantes are aggressively selling government debt');
  if (subs.volatility > 60) triggers.push('bond market volatility is at crisis levels');
  if (subs.liquidity > 60) triggers.push('financial system liquidity is drying up');

  const triggerText = triggers.length > 0
    ? `Key danger signals: ${triggers.join('; ')}.`
    : 'Multiple critical indicators have crossed into danger zones.';

  return {
    headline: 'Danger — Bond Market Flashing Major Warnings',
    body: `The bond market is sending serious distress signals. ${triggerText} In past episodes with similar readings, stocks experienced significant declines. Smart money has moved to the sidelines. This is not the time for aggressive positioning — capital preservation should be your top priority. Composite risk score: ${compositeScore}/100.`,
    tone: 'danger',
  };
}
